<template>
  <v-container class="pa-0">
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-row>
            <v-col
              v-if="!$vuetify.breakpoint.smAndDown"
              cols="auto"
              class="pl-12"
            >
              <v-card-text>
                <v-img :src="$images.SURVEY_INCOMPLETE" width="120"></v-img>
              </v-card-text>
            </v-col>
            <v-col>
              <v-card-title>
                Heatmap 101: How to read this report?
              </v-card-title>
              <v-card-text>
                Our heatmap report is a great way to identify your companies
                strengths and weaknesses, but before you draw any conclusions,
                we highly recommend reviewing our heatmap help article.
              </v-card-text>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    <loading-spinner v-if="loading || _.isEmpty(heatmap)"> </loading-spinner>
    <v-row v-else>
      <v-col cols="12" v-if="!hideProgress">
        <v-card>
          <v-card-text class="py-0 secondary--text">
            <v-row>
              <v-col cols="auto" class="my-auto">
                <v-img :src="$images.PULSE_ICON"></v-img>
              </v-col>
              <v-col>
                <v-card-title>
                  Culture Tracking Progress
                </v-card-title>
                <v-card-subtitle>
                  Your culuture tracking survey is collecting data
                </v-card-subtitle>
              </v-col>
              <v-spacer class="limited-spacer"></v-spacer>
              <v-divider vertical></v-divider>
              <v-col cols="auto" class="my-auto">
                <div class="subtitle-1 font-weight-bold">
                  Activity
                  <v-icon small color="secondary">directions_bike</v-icon>
                </div>
                <div>
                  {{ heatmap.overview.totalResponses }} responses have been
                  collected
                </div>
                <div>
                  {{ heatmap.overview.totalUserResponses }} of
                  {{ heatmap.overview.totalUsers }} members have contributed
                </div>
              </v-col>
              <v-divider vertical></v-divider>
              <v-col cols="auto" class="my-auto">
                <v-row dense>
                  <v-col
                    class="subtitle-1 mb-2 text-no-wrap"
                    v-bind:style="{
                      color: $coreScores.getColor(
                        $coreScores.reverseFormat(
                          heatmap.overview.confidenceScore
                        )
                      )
                    }"
                  >
                    <span class="font-weight-bold secondary--text"
                      >Status:
                    </span>
                    {{ confidenceText(heatmap.overview.confidenceScore) }}
                  </v-col>
                  <v-spacer class="limited-spacer"></v-spacer>
                  <v-col cols="auto">
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          color="lighttext"
                          small
                          class=" ml-2"
                        >
                          help
                        </v-icon>
                      </template>
                      <div class="tooltip-limit-width">
                        {{
                          confidenceHelpText(heatmap.overview.confidenceScore)
                        }}
                      </div>
                    </v-tooltip>
                  </v-col>
                </v-row>
                <v-progress-linear
                  class="rounded-pill my-auto elevation-1"
                  :value="heatmap.overview.confidenceScore"
                  background-color="white"
                  :color="
                    $coreScores.getColor(
                      $coreScores.reverseFormat(
                        heatmap.overview.confidenceScore
                      )
                    )
                  "
                  height="16"
                ></v-progress-linear>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" class="secondary--text">
        <v-card>
          <v-row dense>
            <v-col cols="auto">
              <v-card-text class="pr-0">
                <v-img :src="$images.HEATMAP" width="30"></v-img>
              </v-card-text>
            </v-col>
            <v-col>
              <v-card-title>
                {{ title }}
              </v-card-title>
            </v-col>
            <v-spacer class="limited-spacer"></v-spacer>
            <v-col
              class="my-auto pr-2"
              :cols="$vuetify.breakpoint.smAndDown ? '12' : 'auto'"
            >
              <v-btn color="secondary" @click="exportHeatmap">
                Export
              </v-btn>
            </v-col>
            <v-col
              class="my-auto pr-4"
              :cols="$vuetify.breakpoint.smAndDown ? '12' : 'auto'"
            >
              <timeframe-select
                v-model="filter"
                v-on:change="refreshData"
              ></timeframe-select>
            </v-col>
          </v-row>
          <v-divider></v-divider>
          <div class="heatmap-scroll">
            <div class="heatmap-wrap">
              <table class="heatmap">
                <heatmap-col-group :headers="headers"></heatmap-col-group>
                <heatmap-head
                  :headers="headers"
                  :selectedColumns="selectedColumns"
                  v-on:select="select"
                  :companies="companies"
                >
                  <v-container v-if="!companies">
                    <v-row>
                      <v-col cols="auto">
                        Breakdown by:
                        <v-select
                          v-model="filter.breakdownId"
                          solo
                          outlined
                          flat
                          hide-details
                          dense
                          :items="filter_items"
                          item-text="name"
                          item-value="id"
                          label="Filter type"
                          color="secondary"
                          class="min-width"
                          @change="refreshData"
                          :disabled="!settings.isNonLitePlan"
                        >
                        </v-select>
                      </v-col>
                    </v-row>
                  </v-container>
                </heatmap-head>
                <tbody>
                  <heatmap-row
                    count
                    title="Number of Surveys Completed"
                    :standard_value="{ value: standard_count }"
                    :values="counts"
                    dark
                    small
                  >
                  </heatmap-row>
                  <heatmap-row
                    title="Overall Score"
                    bold
                    :standard_value="standard_value({ id: null })"
                    :values="values({ id: null })"
                  >
                  </heatmap-row>
                  <template v-for="(item, i) in items">
                    <heatmap-row
                      :key="i"
                      :title="item.name"
                      :standard_value="standard_value(item)"
                      :values="values(item)"
                      clickable
                      :selected="expanded_categories.includes(item.id)"
                      v-on:click="expandRow(item.id)"
                      :expand="item.childRows && item.childRows.length > 0"
                      v-on:cellclick="showDialog"
                    >
                      <template v-slot:titleExtension>
                        <v-tooltip top v-if="item.description">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              color="lighttext"
                              small
                              class=" ml-2"
                            >
                              help
                            </v-icon>
                          </template>
                          <div class="tooltip-limit-width">
                            {{ t2(item.description) }}
                          </div>
                        </v-tooltip>
                      </template>
                    </heatmap-row>
                    <template v-for="(subItem, k) in item.childRows">
                      <heatmap-row
                        :key="i + 'childrow' + k"
                        :class="
                          expanded_categories.includes(item.id) ? '' : 'd-none'
                        "
                        :title="subItem.name"
                        :standard_value="standard_child_value(item, subItem)"
                        :values="child_values(item, subItem)"
                        selected
                        indent
                        v-on:cellclick="showDialog"
                      >
                        <template v-slot:titleExtension>
                          <v-tooltip top v-if="subItem.description">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                                color="lighttext"
                                small
                                class=" ml-2"
                              >
                                help
                              </v-icon>
                            </template>
                            <div class="tooltip-limit-width">
                              {{ subItem.description }}
                            </div>
                          </v-tooltip>
                        </template>
                      </heatmap-row>
                    </template>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
    <pulse-heatmap-dialog
      ref="dialog"
      :companies="companies"
    ></pulse-heatmap-dialog>
  </v-container>
</template>
<script>
import HeatmapRow from "@/components/common/HeatmapRow";
import HeatmapColGroup from "@/components/common/HeatmapColGroup";
import HeatmapHead from "@/components/common/HeatmapHead";
import { mapGetters } from "vuex";
import TimeframeSelect from "@/components/reporting/TimeframeSelect";
import PulseHeatmapDialog from "@/components/pulse/PulseHeatmapDialog";
import reports from "@/helpers/mixins/reports";
export default {
  name: "PulseHeatmap",
  components: {
    HeatmapRow,
    HeatmapColGroup,
    HeatmapHead,
    TimeframeSelect,
    PulseHeatmapDialog
  },
  props: {
    filter: { type: Object },
    hideProgress: { type: Boolean, default: false },
    title: { type: String, default: "Culture Heatmap" },
    expandOnLoad: { type: Boolean, default: false },
    companies: { type: Boolean, default: false }
  },
  mixins: [reports],
  data: () => ({
    loading: false,
    expanded_categories: [],
    selectedColumns: []
  }),
  computed: {
    filter_items() {
      let list = [{ id: "department", name: "Departments" }];

      if (this.settings.showManagers) {
        list.push({ id: "manager", name: "Teams" });
      }

      list.push({ id: "level", name: "Levels" });

      list = list.concat(
        (this.settings.customUserFields || [])
          .filter(x => x.includeInReporting)
          .map(x => ({
            id: x.id,
            name: x.name
          }))
      );

      return list;
    },
    items() {
      return this.heatmap.rows;
    },
    standard_count() {
      return (this.heatmap.counts.find(y => y.id == null) || { count: 0 })
        .count;
    },
    standard_value() {
      return item => {
        let score = (
          this.heatmap.items.find(
            y =>
              y.pulseCategoryId == item.id &&
              y.id == null &&
              y.relatedId == null
          ) || { score: 0 }
        ).score;

        return {
          value: score,
          pulseCategoryId: item.id,
          id: null
        };
      };
    },
    standard_child_value() {
      return (item, subItem) => {
        let score = (
          this.heatmap.items.find(
            y =>
              y.pulseCategoryId == item.id &&
              y.id == null &&
              y.relatedId == subItem.id
          ) || { score: 0 }
        ).score;

        return {
          value: score,
          pulseCategoryId: item.id,
          id: null,
          relatedId: isNaN(subItem.id) ? subItem.id : null
        };
      };
    },
    counts() {
      return this.heatmap.columns.map(x => {
        let score = (
          this.heatmap.counts.find(y => y.id == x.id) || { count: 0 }
        ).count;

        return {
          value: score,
          hide: x.parentId && this.selectedColumns.indexOf(x.parentId) < 0
        };
      });
    },
    values() {
      return item => {
        return this.heatmap.columns.map(x => {
          let score = (
            this.heatmap.items.find(
              y =>
                y.pulseCategoryId == item.id &&
                y.id == x.id &&
                y.relatedId == null
            ) || { score: 0 }
          ).score;

          return {
            value: score,
            hide: x.parentId && this.selectedColumns.indexOf(x.parentId) < 0,
            hideValues: x.hideValues,
            pulseCategoryId: item.id,
            id: x.id
          };
        });
      };
    },
    child_values() {
      return (item, subItem) => {
        return this.heatmap.columns.map(x => {
          let score = (
            this.heatmap.items.find(
              y =>
                y.pulseCategoryId == item.id &&
                y.id == x.id &&
                y.relatedId == subItem.id
            ) || { score: 0 }
          ).score;

          return {
            value: score,
            hide: x.parentId && this.selectedColumns.indexOf(x.parentId) < 0,
            hideValues: x.hideValues,
            pulseCategoryId: item.id,
            id: x.id,
            relatedId: isNaN(subItem.id) ? subItem.id : null
          };
        });
      };
    },
    headers() {
      let list = [
        { value: "name", text: "", sortable: false, width: 250 },
        {
          value: "overall",
          text: this.companies ? "All Companies" : "All Company",
          sortable: false,
          angled: true,
          width: 30,
          disabled: false,
          bold: true
        }
      ];

      list = list.concat(
        this.heatmap.columns.map(x => ({
          value: x.id,
          parentValue: x.parentId,
          text: x.name,
          sortable: false,
          angled: true,
          width: 30,
          disabled: x.childCount == 0,
          expand: x.childCount > 0
        }))
      );

      list.push({ value: "empty", text: "" });

      return list;
    },
    ...mapGetters({
      heatmap: "reporting/heatmap",
      profile: "profile/profile",
      settings: "profile/settings"
    })
  },
  created() {
    this.refreshData();
  },
  methods: {
    showDialog(item) {
      let filter = this._.cloneDeep(this.filter);
      filter.pulseCategoryId = item.pulseCategoryId;
      filter.id = item.id;
      filter.relatedId = item.relatedId;
      this.$refs.dialog.showDialog(filter);
    },
    expandRow(id) {
      let index = this.expanded_categories.indexOf(id);
      if (index < 0) {
        this.expanded_categories.push(id);
      } else {
        this.expanded_categories.splice(index, 1);
      }
    },
    refreshData() {
      this.loading = true;
      this.$store
        .dispatch(
          this.companies
            ? "reporting/getPulseCompanyHeatmap"
            : "reporting/getPulseHeatmap",
          this.filter
        )
        .then(() => {
          if (this.expandOnLoad) {
            this.expanded_categories = this.items.map(x => x.id);
          }
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading pulse heatmap."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    select(header) {
      if (header.disabled) {
        return;
      }
      let index = this.selectedColumns.indexOf(header.value);
      if (header.value == "overall") {
        this.selectedColumns = [];
      } else if (index >= 0) {
        let idsToRemove = [header.value];
        this._.each(this.headers, function(item) {
          if (idsToRemove.indexOf(item.parentValue) >= 0) {
            idsToRemove.push(item.value);
          }
        });
        this.selectedColumns = this.selectedColumns.filter(
          x => idsToRemove.indexOf(x) < 0
        );
      } else {
        this.selectedColumns.push(header.value);
      }
    },
    exportHeatmap() {
      this.$store
        .dispatch(
          this.companies
            ? "reporting/exportPulseCompanyHeatmap"
            : "reporting/exportPulseHeatmap",
          this.filter
        )
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while exporting your members."
          );
        });
    }
  }
};
</script>
